import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const benefits = [
  {
    icon: "fas fa-map-marked-alt",
    title: "Prime plots in Yas Island, Saadiyat Island, and Al Shamkha",
  },
  {
    icon: "fas fa-city",
    title: "Residential and commercial plots for every goal",
  },
  {
    icon: "fas fa-chart-line",
    title: "Backed by strong government policy and continuous development",
  },
  {
    icon: "fas fa-coins",
    title: "Real growth potential and long-term value",
  },
];

const buyingSteps = [
  ["Understand Your Requirements", "We discuss your budget, preferred locations, and land use goals."],
  ["Curated Plot Selection", "You receive a shortlist of land options that match your criteria."],
  ["Site Visits", "We arrange visits so you can explore each plot in detail."],
  ["Negotiation and Closing", "We guide you through offers, paperwork, and final ownership transfer."],
];

const faqs = [
  {
    icon: "fas fa-globe",
    question: "Can foreigners buy land in Abu Dhabi?",
    answer: "Yes. Foreign nationals can purchase land in designated freehold areas with full ownership rights.",
  },
  {
    icon: "fas fa-map",
    question: "What types of land are available in Abu Dhabi?",
    answer: "You can find residential, commercial, waterfront, and mixed-use plots across various prime locations.",
  },
  {
    icon: "fas fa-chart-line",
    question: "Is buying land in Abu Dhabi a good investment?",
    answer: "Yes. Land offers strong capital appreciation and flexibility for future development.",
  },
  {
    icon: "fas fa-handshake",
    question: "How does Nova Vision Real Estate help buyers?",
    answer: "We are a client-focused real estate company. Helping you buy the best property matching your needs is our commitment.",
  },
  {
    icon: "fas fa-clipboard-check",
    question: "What should I check before buying land?",
    answer: "You must go through zoning laws, ownership documents, and infrastructure availability before making a purchase.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Land for Sale Abu Dhabi", item: "https://nvrealestate.ae/land-for-sale-abu-dhabi" },
  ],
};

function LandForSaleAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Land for Sale Abu Dhabi – Plots & Investment UAE</title>
        <meta
          name="description"
          content="Do you want to buy land for sale in Abu Dhabi? NV Real Estate team is dedicated to helping you buy the best land at the best location. Let us connect now."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <meta property="og:title" content="Land for Sale Abu Dhabi – Invest in Your Future Today" />
        <meta property="og:description" content="Find the right plot of land in Abu Dhabi with a curated search, verified listings, and expert support from inquiry to transfer." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://nvrealestate.ae/land-for-sale-abu-dhabi" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/land-for-sale-abu-dhabi/02.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi land investment specialists</span>
              <h1 id="villa-page-title">Land for Sale Abu Dhabi – Invest in Your Future Today</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Sale&city=&developer=&developer_name=&project=&project_name=&property_type=Land&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore plots
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/land-for-sale-abu-dhabi/02.png"
                alt="Land plots and development sites in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Off-market access</strong>
                <span>Including plots not listed anywhere else</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Land for Sale Abu Dhabi</h2>
              <p>
                Nova Vision Real Estate is one of the most trusted and appreciated real estate
                companies in Abu Dhabi. Both residents and investors prefer us for their property
                purchase requirements because they know we have access even to properties that are
                not listed anywhere. Reaching out to us means having access to more options. We
                listen to your needs, preferences, and budget carefully, then filter our database to
                find the best land for sale in Abu Dhabi for you. We recommend a few options and
                facilitate exploring them so you can make the right choice &mdash; and our support
                continues well after you decide.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="villa-benefits-title">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why buy land here</span>
              <h2 id="villa-benefits-title">Why Buy Land for Sale in Abu Dhabi?</h2>
              <p>
                Abu Dhabi is one of the fastest-growing cities in the UAE, offering one of the most
                secure and investor-friendly real estate environments in the region. The market is
                supported by strong government policies, continuous development, and increasing
                demand from local and international buyers &mdash; with rising interest in{" "}
                <Link to="/archive-properties/buy-house-abu-dhabi/">high-value real estate</Link> and
                land investments strengthening the outlook further. Whether you're planning to build
                your dream home, develop a commercial project, or secure a long-term investment, the
                city offers residential and commercial plots across areas like Yas Island, Saadiyat
                Island, and Al Shamkha.
              </p>
            </div>
            <div className="villa-benefits__grid">
              {benefits.map((benefit, index) => (
                <article className="villa-benefit-card" key={benefit.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={benefit.icon} /></span>
                  <h3>{benefit.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-process villa-section" aria-labelledby="villa-process-title">
          <div className="container villa-process__layout">
            <div className="villa-process__aside">
              <span className="villa-kicker">Your investment journey</span>
              <h2 id="villa-process-title">A Buying Process That Works for You</h2>
              <p>Buying land should feel clear and manageable. Our process is built to simplify every step.</p>
              <Link className="villa-text-link" to="/contact-us">Speak with an advisor <span aria-hidden="true">-&gt;</span></Link>
            </div>
            <div className="villa-process__steps-wrap">
              <ol className="villa-process__steps">
                {buyingSteps.map(([title, text], index) => (
                  <li key={title}>
                    <span className="villa-process__number">{String(index + 1).padStart(2, "0")}</span>
                    <div><h3>{title}</h3><p>{text}</p></div>
                  </li>
                ))}
              </ol>
              <p className="villa-process__closing">This structured approach ensures you make informed decisions with confidence.</p>
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Buy Land for Sale in Abu Dhabi from NV Real Estate?</h2>
                <p>
                  NV Real Estate is known for its professionalism, transparency, and commitment to
                  client success. We focus on delivering value-driven solutions that ensure your
                  investment is secure, profitable, and aligned with your long-term objectives.
                </p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">Call us now at +971-501577822 to share your requirements and get the best solutions.</p>
              </div>
              <ul>
                <li>Strong relationships with top developers and landowners</li>
                <li>Verified listings with clear legal documentation</li>
                <li>Deep understanding of the Abu Dhabi real estate market</li>
                <li>Honest, strategic advice tailored to your goals</li>
                <li>Complete support from inquiry to final transaction</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Buyer questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your land search with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`land-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`land-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-map-marked-alt" /><div><strong>Curated Plots</strong><span>Matched to Your Brief</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Purchase Support</strong><span>Inquiry to Transfer</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email your requirements to us at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.</p>
          </div>
        </section>
      </main>

      <CTA />
    </>
  );
}

export default LandForSaleAbuDhabi;